import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PRIORITIES,
  STAGES,
  STAGE_COLORS,
  STAGE_LABELS,
  urgencyOf,
  type Priority,
  type Task,
  type TaskActivity,
} from "@board/shared";
import { api } from "./api";
import { forgetClick, isSecondClick } from "./doubleClick";
import { useBoard } from "./store";

const PRIORITY_LABELS: Record<Priority, string> = {
  urgent: "Urgent",
  high: "High",
  normal: "Normal",
  low: "Low",
};
const TITLE_SAVE_DELAY = 600;

function activityTime(value: string): string {
  return new Date(value).toLocaleString("en-CA", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** A sticky opened into a readable, screen-space workspace. */
export function TaskSheet({
  task,
  onExpand,
}: {
  task: Task;
  /** The second half of a double-click on the note this sheet was opened from. */
  onExpand: (task: Task) => void;
}) {
  const users = useBoard((s) => s.users);
  const me = useBoard((s) => s.me);
  const isAdmin = useBoard((s) => s.isAdmin());
  const allSubtasks = useBoard((s) => s.subtasks);
  const allComments = useBoard((s) => s.comments);
  const clients = useBoard((s) => s.clients);
  const worklists = useBoard((s) => s.worklists);
  const taskWorklists = useBoard((s) => s.taskWorklists);
  const openTask = useBoard((s) => s.openTask);
  const updateTask = useBoard((s) => s.updateTask);
  const deleteTask = useBoard((s) => s.deleteTask);
  const addSubtask = useBoard((s) => s.addSubtask);
  const updateSubtask = useBoard((s) => s.updateSubtask);
  const deleteSubtask = useBoard((s) => s.deleteSubtask);
  const addComment = useBoard((s) => s.addComment);
  const deleteComment = useBoard((s) => s.deleteComment);
  const unlinkTaskFromWorklist = useBoard((s) => s.unlinkTaskFromWorklist);

  const [title, setTitle] = useState(task.title);
  const [newStep, setNewStep] = useState("");
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [mobilePane, setMobilePane] = useState<"task" | "activity">("task");
  const [activity, setActivity] = useState<TaskActivity[]>([]);
  const [activityState, setActivityState] = useState<"loading" | "ready" | "error">("loading");
  const editing = useRef(false);

  const client = clients.find((candidate) => candidate.id === task.client_id) ?? null;
  const homeWorklist = worklists.find((candidate) => candidate.id === task.worklist_id) ?? null;
  const homeName = client?.name ?? homeWorklist?.name ?? "Task";
  const subtasks = useMemo(
    () => allSubtasks.filter((step) => step.task_id === task.id).slice().sort((a, b) => a.position - b.position),
    [allSubtasks, task.id],
  );
  const comments = useMemo(
    () => allComments.filter((comment) => comment.task_id === task.id),
    [allComments, task.id],
  );
  const done = subtasks.filter((step) => step.done).length;
  const urgency = urgencyOf(task);
  const linkedWorklists = useMemo(
    () => worklists.filter((worklist) => taskWorklists.some(
      (link) => link.task_id === task.id && link.worklist_id === worklist.id,
    )),
    [task.id, taskWorklists, worklists],
  );

  const refreshActivity = useCallback(async () => {
    try {
      const result = await api.tasks.activity(task.id);
      setActivity(result.activity);
      setActivityState("ready");
    } catch {
      setActivityState("error");
    }
  }, [task.id]);

  useEffect(() => {
    setActivityState("loading");
    void refreshActivity();
  }, [refreshActivity]);

  useEffect(() => {
    if (!editing.current) setTitle(task.title);
  }, [task.title]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !(event.target as HTMLElement)?.closest("input, textarea, select")) {
        openTask(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openTask]);

  const save = useCallback(
    (patch: Partial<Task>) => {
      setProblem(null);
      return updateTask(task.id, patch)
        .then(() => refreshActivity())
        .catch(() => setProblem("That change didn't save."));
    },
    [refreshActivity, task.id, updateTask],
  );

  useEffect(() => {
    if (!editing.current || title.trim() === task.title) return;
    const id = window.setTimeout(() => {
      const next = title.trim();
      if (next) void save({ title: next });
    }, TITLE_SAVE_DELAY);
    return () => window.clearTimeout(id);
  }, [save, task.title, title]);

  const timeline = useMemo(() => {
    const taskEvents = activity
      .filter((entry) => entry.message !== "posted a comment" && entry.message !== "edited a comment")
      .map((entry) => ({ kind: "activity" as const, at: entry.at, entry }));
    const messages = comments.map((comment) => ({ kind: "comment" as const, at: comment.created_at, comment }));
    return [...taskEvents, ...messages].sort((a, b) => a.at.localeCompare(b.at));
  }, [activity, comments]);

  return (
    <div
      className="sheet__scrim"
      // Capture, not bubble: the second half of a double-click lands wherever
      // the sheet happens to be over the note — often on the sheet itself,
      // which stops the event before it reaches the scrim. Capture sees it
      // first either way. A press this soon after, and this close to, the one
      // that opened the sheet is a double-click on the note underneath, and it
      // asks for the client's own board rather than for anything in here.
      onPointerDownCapture={(event) => {
        if (!isSecondClick(task.id, event.clientX, event.clientY)) return;
        // Swallowed, so it does not also land in whatever field it was over.
        event.preventDefault();
        event.stopPropagation();
        onExpand(task);
      }}
      onPointerDown={() => openTask(null)}
    >
      <section
        className="sheet"
        style={{ ["--sheet-stage" as string]: STAGE_COLORS[task.stage] }}
        onPointerDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-sheet-title"
      >
        <header className="sheet__topbar">
          <div>
            <span className="sheet__eyebrow">{homeName}</span>
            <span className="sheet__autosave">Changes save automatically</span>
          </div>
          <button
            className="sheet__close"
            onClick={() => {
              forgetClick();
              openTask(null);
            }}
            aria-label="Close task"
          >
            ×
          </button>
        </header>

        <nav className="sheet__mobiletabs" aria-label="Task workspace sections" role="tablist">
          <button
            id="task-sheet-tab-details"
            type="button"
            role="tab"
            aria-controls="task-sheet-details"
            aria-selected={mobilePane === "task"}
            onClick={() => setMobilePane("task")}
          >
            Task
          </button>
          <button
            id="task-sheet-tab-activity"
            type="button"
            role="tab"
            aria-controls="task-sheet-activity"
            aria-selected={mobilePane === "activity"}
            onClick={() => setMobilePane("activity")}
          >
            Activity <span>{timeline.length}</span>
          </button>
        </nav>

        <main
          id="task-sheet-details"
          role="tabpanel"
          aria-labelledby="task-sheet-tab-details"
          className={`sheet__main${mobilePane !== "task" ? " sheet__main--mobile-hidden" : ""}`}
        >
          <textarea
            id="task-sheet-title"
            className="sheet__title"
            value={title}
            rows={2}
            maxLength={200}
            aria-label="Task name"
            onFocus={() => (editing.current = true)}
            onChange={(event) => setTitle(event.target.value)}
            onBlur={() => {
              editing.current = false;
              const next = title.trim();
              if (next && next !== task.title) void save({ title: next });
              else if (!next) setTitle(task.title);
            }}
          />

          <div className="sheet__fields">
            <label className="sheet__field">
              <span>Status</span>
              <select value={task.stage} onChange={(event) => void save({ stage: event.target.value as Task["stage"] })}>
                {STAGES.map((stage) => <option key={stage} value={stage}>{STAGE_LABELS[stage]}</option>)}
              </select>
            </label>
            <label className="sheet__field">
              <span>Assignee</span>
              <select value={task.assignee_id ?? ""} onChange={(event) => void save({ assignee_id: event.target.value || null })}>
                <option value="">Nobody</option>
                {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
              </select>
            </label>
            <label className="sheet__field">
              <span>Due date</span>
              <span className="sheet__fieldline">
                <input type="date" value={task.due_date ?? ""} onChange={(event) => void save({ due_date: event.target.value || null })} />
                {urgency !== "normal" ? <b className={`sheet__flag sheet__flag--${urgency}`}>{urgency === "overdue" ? "Overdue" : "Due soon"}</b> : null}
              </span>
            </label>
            <label className="sheet__field">
              <span>Priority</span>
              <select value={task.priority} onChange={(event) => void save({ priority: event.target.value as Priority })}>
                {PRIORITIES.map((priority) => <option key={priority} value={priority}>{PRIORITY_LABELS[priority]}</option>)}
              </select>
            </label>
          </div>

          <section className="sheet__block sheet__placements">
            <h2>Appears on <span>{1 + linkedWorklists.length}</span></h2>
            <ul>
              <li><span className="sheet__placementhome">Home</span><strong>{homeName}</strong></li>
              {linkedWorklists.map((worklist) => <li key={worklist.id}>
                <span className="sheet__placementlink">Linked</span><strong>{worklist.name}</strong>
                <button type="button" aria-label={`Remove from ${worklist.name}`} onClick={() => {
                  setProblem(null);
                  void unlinkTaskFromWorklist(task.id, worklist.id)
                    .then(() => refreshActivity())
                    .catch(() => setProblem("That linked copy couldn’t be removed."));
                }}>Remove</button>
              </li>)}
            </ul>
            {linkedWorklists.length === 0 ? <p>Drag this sticky onto another worklist to show it there too.</p> : null}
          </section>

          <section className="sheet__block">
            <h2>Subtasks <span>{done} of {subtasks.length}</span></h2>
            <div className="sheet__progress"><span style={{ width: subtasks.length ? `${(done / subtasks.length) * 100}%` : "0%" }} /></div>
            <ul className="steps">
              {subtasks.map((step) => (
                <li key={step.id} className={step.done ? "step step--done" : "step"}>
                  <label>
                    <input type="checkbox" checked={step.done} onChange={(event) => {
                      void updateSubtask(step.id, { done: event.target.checked })
                        .then(() => refreshActivity())
                        .catch(() => setProblem("That subtask didn't save."));
                    }} />
                    <span>{step.title}</span>
                  </label>
                  {isAdmin || step.created_by === me?.id ? (
                    <button aria-label={`Remove ${step.title}`} onClick={() => {
                      void deleteSubtask(step.id).then(() => refreshActivity()).catch(() => setProblem("That subtask couldn't be removed."));
                    }}>×</button>
                  ) : null}
                </li>
              ))}
            </ul>
            <form className="step__add" onSubmit={(event) => {
              event.preventDefault();
              const value = newStep.trim();
              if (!value) return;
              setNewStep("");
              void addSubtask(task.id, value).then(() => refreshActivity()).catch(() => {
                setNewStep(value);
                setProblem("That subtask didn't save.");
              });
            }}>
              <span aria-hidden="true">＋</span>
              <input value={newStep} placeholder="Add a subtask" maxLength={200} onChange={(event) => setNewStep(event.target.value)} />
            </form>
          </section>

          <footer className="sheet__foot">
            {problem ? <span role="alert">{problem}</span> : <span />}
            {isAdmin || task.created_by === me?.id ? (
              <button onClick={() => void deleteTask(task.id).catch(() => setProblem("That task couldn't be moved to the recycle bin."))}>Move to recycle bin</button>
            ) : null}
          </footer>
        </main>

        <aside
          id="task-sheet-activity"
          role="tabpanel"
          aria-labelledby="task-sheet-tab-activity"
          className={`sheet__conversation${mobilePane !== "activity" ? " sheet__conversation--mobile-hidden" : ""}`}
        >
          <header><h2>Activity & comments</h2><span>{timeline.length}</span></header>
          <ol className="timeline" aria-live="polite">
            {activityState === "loading" ? <li className="timeline__empty">Loading activity…</li> : null}
            {activityState === "error" ? <li className="timeline__empty">Activity couldn't be loaded.</li> : null}
            {activityState === "ready" && timeline.length === 0 ? <li className="timeline__empty">No activity yet.</li> : null}
            {timeline.map((item) => {
              if (item.kind === "activity") {
                const actor = item.entry.actor_name ?? (item.entry.actor_kind === "agent" ? "An assistant" : "System");
                return <li className="timeline__event" key={`a-${item.entry.id}`}><span className="timeline__dot" /><p><strong>{actor}</strong> {item.entry.message}</p><time>{activityTime(item.at)}</time></li>;
              }
              const author = users.find((user) => user.id === item.comment.author_id);
              return (
                <li className="timeline__comment" key={`c-${item.comment.id}`}>
                  <div><strong>{author?.name ?? "Someone"}</strong><time>{activityTime(item.at)}</time></div>
                  <p>{item.comment.body}</p>
                  {isAdmin || item.comment.author_id === me?.id ? <button aria-label="Delete comment" onClick={() => {
                    void deleteComment(item.comment.id).then(() => refreshActivity()).catch(() => setProblem("That comment couldn't be removed."));
                  }}>Delete</button> : null}
                </li>
              );
            })}
          </ol>
          <form className="commentbox" onSubmit={(event) => {
            event.preventDefault();
            const value = draft.trim();
            if (!value) return;
            setDraft("");
            void addComment(task.id, value).then(() => refreshActivity()).catch(() => {
              setDraft(value);
              setProblem("That comment didn't send.");
            });
          }}>
            <textarea value={draft} rows={3} maxLength={4000} placeholder="Write a comment…" aria-label="Write a comment" onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) event.currentTarget.form?.requestSubmit();
            }} />
            <div><span>Ctrl + Enter to post</span><button type="submit" disabled={!draft.trim()}>Post</button></div>
          </form>
        </aside>
      </section>
    </div>
  );
}

export function TaskSheetHost({ onExpand }: { onExpand: (task: Task) => void }) {
  const focusedTaskId = useBoard((s) => s.focusedTaskId);
  const tasks = useBoard((s) => s.tasks);
  const task = tasks.find((candidate) => candidate.id === focusedTaskId) ?? null;
  return task ? <TaskSheet key={task.id} task={task} onExpand={onExpand} /> : null;
}
